import { Box, Link, ListItem, Stack, Typography, List } from "@mui/material";
import { HiOutlineTicket } from "react-icons/hi";
import { MdOutlineEventSeat, MdOutlinedFlag } from "react-icons/md";
import { TbShirtSport } from "react-icons/tb";
import { createJsxLink } from "@/global/utils/utils.dom";
import { CartManagerState } from "@/global/store/data/purchaseSeats/cartManager";
import Head from "next/head";
import { TeamConfig } from "@/global/hooks/useTeamConfig";
import constants from "@/global/config/constants";
import React from "react";
import { MLBAdobeScriptHeader } from "@/global/common/mlb-common/MLBAdobeScriptHeader";
import { createState } from "@/global/store/zustand_store";
import { NotificationModal } from "@/shared/common/InfoPopup/NotificationModal";
import { Divider } from "@/shared/base/components/Divider/Divider";

export const purchaseSeatsLanguages = {
  progressBarAditionalText: " to continue!",
  progressBarSuccessText:
    "You're good to go! Add more games or select Continue to view seating options.",
};

export const purchaseGiftCardLanguages = {
  pageTitle: "Give the gift of Diamondbacks Baseball",
  mainCardTitle: "Share a Gift",
  mainCardDescription:
    "Share the magic of Diamondbacks baseball with a friend or loved one. Upon purchase of the gift, a unique code will be generated for the intended recipient to redeem. Once redeemed, the gift is non-refundable.",
  amountTitle: "Gift Amount",
  recipientTitle: "Recipient",
  recipientEnabledDesc:
    "The Recipient will be sent an email with the unique code for them to redeem their gift. Once redeemed, the recipient will receive an account credit that they can use to purchase tickets for all Arizona Diamondbacks games at Chase Field.",
  recipientDisabledDesc:
    "If an intended recipient is not specified, once purchased, you will have to forward the unique code to the recipient. Upon your recipient receiving their gift, they will be prompted to enter their unique code to redeem the gift. Once redeemed, the recipient will receive an account credit that they use to purchase tickets for all Arizona Diamondbacks games at Chase Field.",
};

export const redeemGiftCardLanguages = {
  pageTitle: "Give the gift of Diamondbacks Baseball",
  mainCardTitle: "Redeem Gift",
  mainCardLoggedInDesc:
    "Please input the case sensitive code provided to redeem the value of the gift card to the Hippodrome Broadway Series in Baltimore account above.",
  mainCardLoggedOutDesc:
    "Please input the case sensitive unique code provided to redeem the value of the gift card to the Ticketmaster.",
  successDesc: "has been added to to your account!",
};

export const howItWorks = [
  {
    icon: <MdOutlinedFlag />,
    title: "View Available Suites",
    description: "Shop for available suites at Chase Field.",
  },
  {
    icon: <MdOutlineEventSeat />,
    title: "Login to Account Manager",
    description:
      " Click Continue and connect your Arizona Diamondbacks account using our secure Ticketmaster login.",
  },
  {
    icon: <TbShirtSport />,
    title: "Select Your Suites",
    description: "Select the suites you wish to purchase for each game.",
  },

  {
    icon: <HiOutlineTicket />,
    title: "Get Your Tickets",
    description:
      "Tickets will be available in your Account Manager within 24 hours of purchase. Reach out to Live Fan Support for any questions.",
  },
];

export const faqs = [
  {
    title: "What is included with my suite reservation?",
    description: (
      <>
        <Box component="span" sx={{ display: "block", fontWeight: 600 }}>
          D-backs Games:
        </Box>
        <Box component="span" sx={{ display: "block", mb: 1.5 }}>
          All suites include private seating, game tickets, parking passes, and
          complimentary food and beverage. The number of tickets and parking
          passes vary by suite. For more details on specific amenities, click
          the &quot;More Info&quot; tab below each suite.
        </Box>
        <Box component="span" sx={{ display: "block", fontWeight: 600 }}>
          Concerts:
        </Box>
        <Box component="span" sx={{ display: "block" }}>
          All suites include private seating, event tickets, parking passes, and
          an in-suite food and beverage credit. The number of tickets, parking
          passes, and food and beverage credit vary by event.
        </Box>
      </>
    ),
  },
  {
    title: "Are there other suites available, beyond what I'm seeing?",
    description: (
      <>
        Yes, currently only a limited number of options are available for
        purchase online. Suite options ranging in size from 4 to over 200 guests
        are available each game. For information about our other suite options
        click here, email{" "}
        {createJsxLink("suites@dbacks.com", `mailto:${"suites@dbacks.com"}`)},
        or call {createJsxLink("602.462.4119", "tel:6024624119")}.
      </>
    ),
  },
  {
    title: "What happens after I purchase a D-backs Suite online?",
    description: (
      <>
        Following your purchase, a D-backs representative will follow up with
        you to confirm your order and assist with any questions you have in
        anticipation of your event. Tickets and parking will be added to your
        D-backs account where you'll be able to access them via the MLB Ballpark
        app or in your D-backs account manager by visiting{" "}
        {createJsxLink("www.dbacks.com/myaccount", "www.dbacks.com/myaccount")}.
        All suite tickets are digital.
      </>
    ),
  },
  {
    title: "How do I transfer suite tickets to my guests?",
    description: (
      <>
        To transfer tickets, use your D-backs account manager or the MLB
        Ballpark app. Click here for step-by-step instructions to transfer your
        suite tickets{" "}
        {createJsxLink(
          "https://www.mlb.com/dbacks/apps/ballpark/account-manager",
          "https://www.mlb.com/dbacks/apps/ballpark/account-manager"
        )}
        .
      </>
    ),
  },
  {
    title: "Food and beverage questions?",
    description: (
      <>
        <Box component="span" sx={{ display: "block", mb: 1.5 }}>
          For additional information, regarding F&B, please email{" "}
          {createJsxLink("suites@dbacks.com", `mailto:${"suites@dbacks.com"}`)},
          or call {createJsxLink("602.462.4119", "tel:6024624119")}.
        </Box>
        <Box component="span" sx={{ display: "block", fontWeight: 600 }}>
          D-backs Games:
        </Box>
        <Box component="span" sx={{ display: "block", mb: 1.5 }}>
          D-backs Suite, Studio@122, and Legends Suite reservations made at
          least one week before the game date include access to a fully
          customizable food and beverage menu. Reservations made within one week
          of the game may receive a pre-selected menu of food and beverage.
        </Box>
        <Box component="span" sx={{ display: "block", mb: 1.5 }}>
          Left Field Home Run Porches do not require any advanced ordering prior
          to arrival, though we do recommend guests are logged into the MLB
          ballpark app prior to their arrival to ensure the smoothest experience
          ordering included F&B.
        </Box>
        <Box component="span" sx={{ display: "block", fontWeight: 600 }}>
          Concerts:
        </Box>
        <Box component="span" sx={{ display: "block" }}>
          In lieu of a preset menu, suites for concerts include a food and
          beverage credit that can be applied towards in-suite catering.
          Included catering credits are noted in the suite preview.
        </Box>
      </>
    ),
  },
  {
    title: "Are there additional taxes/fees?",
    description:
      "There are no additional taxes or fees on the pricing you see listed.",
  },
  {
    title: "Have any questions about suites?",
    description: (
      <>
        For additional information, or information about our other suite options
        click{" "}
        {createJsxLink(
          "here",
          "https://www.mlb.com/dbacks/tickets/premium/suites/information/request"
        )}
        , email{" "}
        {createJsxLink("suites@dbacks.com", `mailto:${"suites@dbacks.com"}`)},
        or call {createJsxLink("602.462.4119", "tel:6024624119")}.
      </>
    ),
  },
  {
    title: "Terms & Conditions",
    description: (
      <>
        Full terms and conditions of suite rentals can be found by visiting{" "}
        {createJsxLink(
          "https://www.dbacks.com/suiteagreement",
          "https://www.dbacks.com/suiteagreement"
        )}
        .
      </>
    ),
  },
];

export function TermsAndCondition() {
  return (
    <>
      By purchasing you agree to receive SMS alerts regarding ticket delivery
      and agree to the{" "}
      <Link
        href={
          "https://uploads-ssl.webflow.com/5ed7df6fe67e862e0a62f9f2/616882d2b691e09f75348396_Terms%20%26%20Conditions.pdf"
        }
        target={"_blank"}
        sx={{
          color: "primary.main",
          textDecoration: "none",
          fontWeight: 500,
        }}
      >
        Terms & Conditions
      </Link>{" "}
      and{" "}
      <Link
        href={
          "https://uploads-ssl.webflow.com/5ed7df6fe67e862e0a62f9f2/63c05d80816dac05b5b34ac7_seasonshare%20CCPA%20Privacy%20Policy%20Agreement.pdf"
        }
        target={"_blank"}
        sx={{
          color: "primary.main",
          textDecoration: "none",
          fontWeight: 500,
        }}
      >
        Privacy Policy
      </Link>
      .
    </>
  );
}

export function createPerkTotalLabel(cartManager: CartManagerState) {
  return "Add On Balance";
}

export const AnalyticsHeader = (props: { teamConfig: TeamConfig }) => {
  const { teamSchema } = props.teamConfig;

  return (
    <>
      <MLBAdobeScriptHeader />
      <Head>
        <script
          nonce={process.env.pixelScriptNonce}
          id={`${teamSchema}-meta-pixel-script`}
          defer
          dangerouslySetInnerHTML={{
            __html: `
              !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '1532627770340759');
              fbq('track', 'PageView');
              console.log('meta pixel page load event fired.');
              window.addEventListener("${constants.customEvents.eventPurchaseSuccess}", function(e) {
                try {
                  const { cartRes, checkoutRes } = e.detail;
                  fbq('track', 'Purchase', {
                    content_name: "${teamSchema}_event_purchase", 
                    value: String(cartRes.allInPrice), 
                    currency: 'USD', 
                    order_id: checkoutRes.orderNumber,
                  });
                  console.log('meta pixel purchase event fired.');
                } catch (error) {
                  console.log('Error in meta pixel purchase event', error); 
                }
              });
            `,
          }}
        />

        <noscript>
          <img
            height="1"
            width="1"
            style={{ display: "none" }}
            src="https://www.facebook.com/tr?id=1532627770340759&ev=PageView&noscript=1"
          />
        </noscript>
      </Head>
    </>
  );
};

export const leftFieldPorchSuiteNames = [
  "Left Field Porch 1",
  "Left Field Porch 2",
  "Left Field Porch 3",
  "Left Field Porch 4",
  "Left Field Porch 5",
  "Left Field Porch 6",
  "Left Field Porch 7",
  "Left Field Porch 8",
];

export const studioSuiteNames = ["Studio A", "Studio B"];

// Suites that include only 3 parking passes (rest of the D-backs Suites include 4)
export const threeParkingPassSuiteNumbers = [
  1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 50, 51, 52, 53, 55, 56,
];

export const threeParkingPassSuiteNames = threeParkingPassSuiteNumbers.map(
  (n) => `Suite ${n}`
);

// Suites with custom amenities (18 tickets, 3 overflow, 4 parking passes)
export const threeOverflowSuiteNumbers = [43];

export const threeOverflowSuiteNames = threeOverflowSuiteNumbers.map(
  (n) => `Suite ${n}`
);

export const legendSuiteNames = ["Legends C", "Legends D", "Legends E"];

// Legends Suite F has its own amenities (25 tickets, 15 overflow, 8 parking passes)
export const legendSuiteFNames = ["Legends F"];

export const inventoryCardSuiteModal = createState({
  title: "",
  isOpen: false,
  content: <></>,
});

export const InventoryCard = () => {
  const modal = inventoryCardSuiteModal();
  return (
    <NotificationModal
      hideTitleIcon
      hidePrimaryButton
      title={modal.title}
      hideSecondaryButton
      isOpen={modal.isOpen}
      renderFooter={() => null}
      onClose={() => modal.set({ isOpen: false })}
      paperProps={{ style: { marginLeft: 2, marginRight: 2 } }}
      renderBody={() => <Box p={1}>{modal.content}</Box>}
    />
  );
};

export const LeftFieldPorchContent = () => (
  <Box p={2} pt={1}>
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          minWidth: "135px",
          fontSize: "1.25rem",
          color: "primary.main",
        }}
      >
        LF Home Run Porch
      </Typography>
      <Divider sx={{ flex: 1, marginLeft: 2 }} orientation="horizontal" />
    </Stack>

    <List sx={{ py: 0 }}>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          Bring your baseball glove! The Home Run Porches are located directly
          above the outfield on the center field concourse. Each Porch features
          a unique, over-the-field front row view giving you the chance to catch
          the game winning home run. Arriving early for batting practice is
          encouraged and recommended.
        </Typography>
      </ListItem>

      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          LF Home Run Porch amenities include:
        </Typography>
      </ListItem>

      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          • 4 tickets, option of purchasing up to 2 additional overflow tickets.
          (Overflow tickets can be added during checkout or up until the day of
          your event by contacting a D-backs representative)
          <br />
          • Complimentary menu of food and beverages (including beer & wine)
          delivered to your seats
          <br />
          • 1 parking pass in attached Chase Field Garage
          <br />• Personal television with a live broadcast of the D-backs game
        </Typography>
      </ListItem>
    </List>
  </Box>
);

export const StudioSuiteContent = () => (
  <Box p={2} pt={1}>
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          minWidth: "135px",
          fontSize: "1.25rem",
          color: "primary.main",
        }}
      >
        Studio @122
      </Typography>
      <Divider sx={{ flex: 1, marginLeft: 2 }} orientation="horizontal" />
    </Stack>

    <List sx={{ py: 0 }}>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          Immerse yourself in the crowd while still enjoying the comfort and
          luxury of your own private Suite. The only Suite located on the main
          concourse of Chase Field, Studio@122 is situated directly behind home
          plate allowing you to call balls and strikes right along with the
          umpire.
        </Typography>
      </ListItem>

      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">Studio @122 amenities include:</Typography>
      </ListItem>

      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          • 10 tickets, option of purchasing up to 2 additional overflow
          tickets. (Overflow tickets can be added during checkout or up until
          the day of your event by contacting a D-backs representative)
          <br />
          • Complimentary menu of food and non-alcoholic beverages catered in
          suite
          <br />
          • Two domestic beers per ticket
          <br />• 4 Parking passes located in attached Chase Field Garage
        </Typography>
      </ListItem>
    </List>
  </Box>
);

export const SuitesContent = ({
  parkingPasses = 4,
  tickets = 18,
  overflow = 4,
}: {
  parkingPasses?: number;
  tickets?: number;
  overflow?: number;
}) => (
  <Box p={2} pt={1}>
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          minWidth: "135px",
          fontSize: "1.25rem",
          color: "primary.main",
        }}
      >
        D-backs Suite
      </Typography>
      <Divider sx={{ flex: 1, marginLeft: 2 }} orientation="horizontal" />
    </Stack>

    <List sx={{ py: 0 }}>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          Experience D-backs baseball in comfort from D-backs Suites at Chase
          Field. An all-inclusive experience with a wide variety of vantage
          points available, D-backs Suites are perfect for hosting clients,
          special occasions, or any family outing.
        </Typography>
      </ListItem>

      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          D-backs Suite amenities include:
        </Typography>
      </ListItem>

      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          • {tickets} tickets, option of purchasing up to {overflow} additional
          overflow tickets. (Overflow tickets can be added during checkout or up
          until the day of your event by contacting a D-backs representative)
          <br />
          • Complimentary menu of food & non-alcoholic beverages
          <br />• {parkingPasses} parking passes in attached Chase Field Garage
          <br />• Private in-suite restroom
          <br />• Flat screen television
          <br />• In-suite refrigerator
        </Typography>
      </ListItem>
    </List>
  </Box>
);

export const LegendsContent = ({
  tickets = 50,
  overflow = 25,
  parkingPasses = 15,
}: {
  tickets?: number;
  overflow?: number;
  parkingPasses?: number;
} = {}) => (
  <Box p={2} pt={1}>
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          minWidth: "135px",
          fontSize: "1.25rem",
          color: "primary.main",
        }}
      >
        Legends Suites
      </Typography>
      <Divider sx={{ flex: 1, marginLeft: 2 }} orientation="horizontal" />
    </Stack>

    <List sx={{ py: 0 }}>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          For our largest of groups, the flexible Legends Suites offer a
          premium, private experience with a variety of sizing options. Ideal
          for your next office outing, conference gathering, or customer event,
          The Legends Suite, perched on the upper concourse, provides a
          panoramic view of Chase Field.
        </Typography>
      </ListItem>

      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          Legends Suites amenities include:
        </Typography>
      </ListItem>

      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          • {tickets} tickets, option of purchasing up to {overflow} additional
          overflow tickets. (Overflow tickets can be added during checkout or up
          until the day of your event by contacting a D-backs representative)
          <br />
          • Complimentary menu of food & non-alcoholic beverages
          <br />
          • {parkingPasses} parking passes in attached Chase Field Garage
          <br />• Private restrooms
          <br />• Flat screen television
        </Typography>
      </ListItem>
    </List>
  </Box>
);
